import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAgpMutations, useAgpQuery } from "../hooks/useAgpApi";
import { AttainmentBar, ElapsedNote, PaceChip, SourceTag, formatTarget } from "../components/AgpUi";
import ClientAcquisitionSection, { CA_TEAM_KEY } from "../components/ClientAcquisitionSection";
import { Card, EmptyState, ErrorState, Pill, Skeleton, TeamDot } from "../../components/Ui";
import { teamColor } from "../../metrics";

const CHECKPOINT_KEYS = ["clientMeetings", "taps", "dealsClosed", "curateAttendees", "grossRevenue"];
// Headline drivers on a practice card, in preference order; the first four
// with a target are shown (Client Acquisition has its own set).
const CARD_KEYS = ["clientMeetings", "taps", "dealsClosed", "grossRevenue", "newBusinessPoints", "signedClients", "bdLeads", "newBusinessRevenue"];

const hasTarget = (d) => d && d.target !== null && d.target !== undefined;

export default function AgpOverviewPage({ agpYear, agpMeta }) {
  const navigate = useNavigate();
  const params = useMemo(() => ({ year: agpYear.year }), [agpYear.year]);
  const { data, loading, error, refetch } = useAgpQuery("overview", params);
  const mutations = useAgpMutations();
  const [copying, setCopying] = useState(false);

  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const teams = data ? data.teams.filter((t) => t.hasGoals) : [];
  const teamsWithout = data ? data.teams.filter((t) => !t.hasGoals) : [];
  // practices with a target for at least one checkpoint driver
  const checkpointTeams = teams.filter((t) => t.drivers.some((d) => CHECKPOINT_KEYS.includes(d.key) && hasTarget(d)));
  const caTeam = data ? data.teams.find((t) => t.key === CA_TEAM_KEY) : null;
  const goals = data ? data.goals : [];
  const catalog = data ? data.catalog : [];
  const me = data ? data.me : null;
  const years = agpMeta ? agpMeta.years : [];
  const previousYear = years.filter((y) => y < agpYear.year).sort((a, b) => b - a)[0];

  const copyFromPrevious = async () => {
    if (!previousYear) return;
    setCopying(true);
    await mutations.copyYear(previousYear, agpYear.year);
    setCopying(false);
  };

  return (
    <div className="bd-page bd-agp">
      <div className="bd-page__head">
        <div>
          <h1>AGP Goals {agpYear.year}</h1>
          <p className="bd-page__lede">
            Annual Goal Plan targets by practice and person, with live progress from Salesforce.{" "}
            {data && <ElapsedNote window={data.window} />}
          </p>
        </div>
        <div className="bd-page__head-meta">
          {me && me.isAdmin && data && !teams.length && previousYear && (
            <button type="button" className="bd-btn bd-btn--primary" disabled={copying} onClick={copyFromPrevious}>
              Copy {previousYear} targets into {agpYear.year}
            </button>
          )}
        </div>
      </div>

      {loading && !data ? (
        <Skeleton height={320} />
      ) : !teams.length ? (
        <Card>
          <EmptyState
            title={`No AGP goals for ${agpYear.year} yet`}
            message={
              me && me.canEditAny
                ? "Open a team below to start entering targets, or copy last year's plan as a starting point."
                : "Goals have not been set for this year. Practice leaders can enter them from their team page."
            }
          />
          <div className="bd-agp__team-links">
            {data.teams.map((t) => (
              <Link key={t.key} to={`/landmark/bd/agp/team/${t.key}${agpYear.search}`} className="bd-btn bd-btn--secondary">
                <TeamDot team={t.name} /> {t.practice}
              </Link>
            ))}
          </div>
        </Card>
      ) : (
        <>
          {/* practice cards */}
          <div className="bd-agp__practices">
            {teams.map((t) => {
              const metrics = CARD_KEYS.map((k) => t.drivers.find((d) => d.key === k))
                .filter(hasTarget)
                .slice(0, 4);
              return (
                <Link key={t.key} to={`/landmark/bd/agp/team/${t.key}${agpYear.search}`} className="bd-agp-card" style={{ "--team-color": teamColor(t.name) }}>
                  <div className="bd-agp-card__head">
                    <div>
                      <div className="bd-agp-card__title">
                        <TeamDot team={t.name} /> {t.practice}
                      </div>
                      <div className="bd-agp-card__sub">
                        {t.name} · {t.memberCount} member goals
                      </div>
                    </div>
                    {t.canEdit && <Pill tone="info">can edit</Pill>}
                  </div>
                  {t.coreObjective && <p className="bd-agp-card__objective">{t.coreObjective}</p>}
                  <div className="bd-agp-card__metrics">
                    {metrics.map((d) => (
                      <div key={d.key} className="bd-agp-card__metric">
                        <div className="bd-agp-card__metric-head">
                          <span>{d.label}</span>
                          <PaceChip row={d} compact />
                        </div>
                        <div className="bd-agp-card__metric-values">
                          <strong>{d.actual === null ? "—" : formatTarget(d.actual, d.format)}</strong>
                          <span className="bd-muted"> / {formatTarget(d.target, d.format)}</span>
                        </div>
                        <AttainmentBar row={d} />
                      </div>
                    ))}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* checkpoint */}
          <Card title="Checkpoint" subtitle="Actual vs expected-to-date, by practice" flush>
            <div className="bd-table-wrap">
              <table className="bd-table bd-agp-table">
                <thead>
                  <tr>
                    <th>Practice</th>
                    {CHECKPOINT_KEYS.map((k) => {
                      const def = catalog.find((c) => c.key === k);
                      return (
                        <th key={k} style={{ textAlign: "right" }}>
                          {def ? def.label : k}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {checkpointTeams.map((t) => (
                    <tr key={t.key} className="is-clickable" onClick={() => navigate(`/landmark/bd/agp/team/${t.key}${agpYear.search}`)}>
                      <td>
                        <TeamDot team={t.name} withName />
                        <small className="bd-muted"> {t.practice}</small>
                      </td>
                      {CHECKPOINT_KEYS.map((k) => {
                        const d = t.drivers.find((x) => x.key === k);
                        return (
                          <td key={k} style={{ textAlign: "right" }}>
                            <CheckpointCell row={d} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>
                      <strong>All practices</strong>
                    </td>
                    {CHECKPOINT_KEYS.map((k) => (
                      <td key={k} style={{ textAlign: "right" }}>
                        <CheckpointCell row={data.totals[k]} />
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          {/* Client Acquisition / SGA: its own driver set, measured org-wide */}
          {caTeam && <ClientAcquisitionSection year={agpYear.year} search={agpYear.search} />}

          {/* goal sections */}
          {goals.map((g) => {
            const drivers = catalog.filter((d) => d.goal === g.number);
            return (
              <Card key={g.number} title={`Goal ${g.number}: ${g.title}`} subtitle={g.description} flush>
                <div className="bd-table-wrap">
                  <table className="bd-table bd-agp-table bd-agp-table--goals">
                    <thead>
                      <tr>
                        <th>Key driver</th>
                        {teams.map((t) => (
                          <th key={t.key} style={{ textAlign: "right" }}>
                            <TeamDot team={t.name} /> {t.practice}
                          </th>
                        ))}
                        <th style={{ textAlign: "right" }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drivers.map((def) => {
                        const total = data.totals[def.key];
                        return (
                          <tr key={def.key}>
                            <td>
                              <span className="bd-agp-driver">
                                <span>{def.label}</span>
                                <SourceTag source={def.source} driverKey={def.key} label={def.label} />
                              </span>
                            </td>
                            {teams.map((t) => {
                              const d = t.drivers.find((x) => x.key === def.key);
                              return (
                                <td key={t.key} style={{ textAlign: "right" }}>
                                  <GoalCell row={d} />
                                </td>
                              );
                            })}
                            <td style={{ textAlign: "right" }}>{def.format === "text" ? <span className="bd-muted">—</span> : <GoalCell row={total} />}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })}

          {teamsWithout.length > 0 && (
            <p className="bd-muted bd-agp__footnote">
              No {agpYear.year} goals yet for {teamsWithout.map((t) => t.practice).join(", ")}.{" "}
              {teamsWithout.filter((t) => t.canEdit).length > 0 && (
                <>
                  Set them up:{" "}
                  {teamsWithout
                    .filter((t) => t.canEdit)
                    .map((t) => (
                      <Link key={t.key} to={`/landmark/bd/agp/team/${t.key}${agpYear.search}`} className="bd-link bd-ml-1">
                        {t.practice}
                      </Link>
                    ))}
                </>
              )}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function CheckpointCell({ row }) {
  if (!row || row.target === null || row.target === undefined) return <span className="bd-muted">—</span>;
  return (
    <div className="bd-agp-cell">
      <div className="bd-agp-cell__values">
        <strong>{row.actual === null || row.actual === undefined ? "—" : formatTarget(row.actual, row.format)}</strong>
        <span className="bd-muted"> / {formatTarget(row.target, row.format)}</span>
      </div>
      <div className="bd-agp-cell__meta">
        <small className="bd-muted" title="Where you'd expect to be by now: full-year goal x share of the year elapsed">
          exp. {formatTarget(row.expectedYtd, row.format)} by now
        </small>
        <PaceChip row={row} compact />
      </div>
    </div>
  );
}

function GoalCell({ row }) {
  if (!row) return <span className="bd-muted">—</span>;
  if (row.format === "text") {
    return row.targetText ? <span className="bd-agp-text">{row.targetText}</span> : <span className="bd-muted">—</span>;
  }
  if (row.target === null || row.target === undefined) return <span className="bd-muted">—</span>;
  const hasActual = row.actual !== null && row.actual !== undefined;
  return (
    <div className="bd-agp-cell">
      <div className="bd-agp-cell__values">
        {hasActual ? (
          <>
            <strong>{formatTarget(row.actual, row.format)}</strong>
            <span className="bd-muted"> / {formatTarget(row.target, row.format)}</span>
          </>
        ) : (
          <strong>{formatTarget(row.target, row.format)}</strong>
        )}
      </div>
      {hasActual && (
        <div className="bd-agp-cell__meta">
          <PaceChip row={row} compact />
        </div>
      )}
    </div>
  );
}
